'use client';

import { useState, useMemo } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, Check, Type, FileInput, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { textCaseConverterContent } from '../../data/text-case-converter-content';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type CaseType =
    | 'sentence'
    | 'lower'
    | 'upper'
    | 'title'
    | 'camel'
    | 'pascal'
    | 'snake'
    | 'kebab'
    | 'dot'
    | 'constant'
    | 'train'
    | 'inverse'
    | 'alternating';

// Helper function to convert case names to title case for display
function labelCase(str: string): string {
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
}

export default function TextCaseConverter() {
    const [inputText, setInputText] = useState('');
    const [copied, setCopied] = useState(false);
    const [activeCase, setActiveCase] = useState<CaseType>('sentence');
    const { toast } = useToast();

    const convertCase = (text: string, caseType: CaseType): string => {
        if (!text.trim()) return '';

        switch (caseType) {
            case 'sentence':
                return text
                    .toLowerCase()
                    .replace(/(^\s*\w|[.!?]\s+\w)/g, (match) => match.toUpperCase())
                    .replace(/\s+/g, ' ')
                    .trim();

            case 'lower':
                return text.toLowerCase();

            case 'upper':
                return text.toUpperCase();

            case 'title':
                return text
                    .toLowerCase()
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

            case 'camel':
                return text
                    .toLowerCase()
                    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
                    .replace(/[^a-zA-Z0-9]/g, '');

            case 'pascal':
                return text
                    .toLowerCase()
                    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
                    .replace(/^[a-z]/, (chr) => chr.toUpperCase())
                    .replace(/[^a-zA-Z0-9]/g, '');

            case 'snake':
                return text
                    .toLowerCase()
                    .replace(/\s+/g, '_')
                    .replace(/[^a-z0-9_]/g, '');

            case 'kebab':
                return text
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '');

            case 'dot':
                return text
                    .toLowerCase()
                    .replace(/\s+/g, '.')
                    .replace(/[^a-z0-9.]/g, '');

            case 'constant':
                return text
                    .toUpperCase()
                    .replace(/\s+/g, '_')
                    .replace(/[^A-Z0-9_]/g, '');

            case 'train':
                return text
                    .toLowerCase()
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join('-');

            case 'inverse':
                return text
                    .split('')
                    .map((char) =>
                        char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
                    )
                    .join('');

            case 'alternating':
                return text
                    .toLowerCase()
                    .split('')
                    .map((char, index) => (index % 2 === 0 ? char.toUpperCase() : char))
                    .join('');

            default:
                return text;
        }
    };

    const outputText = useMemo(() => convertCase(inputText, activeCase), [
        inputText,
        activeCase
    ]);

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            const fileContent = event.target?.result as string;
            setInputText(fileContent);
        };
        reader.readAsText(file);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(outputText);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Text has been copied to clipboard.'
        });
        setTimeout(() => setCopied(false), 2000);
    };

    const clearText = () => setInputText('');

    const caseExamples: Record<CaseType, string> = {
        sentence: 'Convert to sentence case',
        lower: 'convert to lowercase',
        upper: 'CONVERT TO UPPERCASE',
        title: 'Convert To Title Case',
        camel: 'convertToCamelCase',
        pascal: 'ConvertToPascalCase',
        snake: 'convert_to_snake_case',
        kebab: 'convert-to-kebab-case',
        dot: 'convert.to.dot.case',
        constant: 'CONVERT_TO_CONSTANT_CASE',
        train: 'Convert-To-Train-Case',
        inverse: 'cONVERT TO iNVERSE cASE',
        alternating: 'CoNvErT tO aLtErNaTiNg CaSe'
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Text Case Converter">
                Convert text between different cases instantly with our free online tool. Transform text to uppercase, lowercase, title case, and more.
            </ToolPageIntro>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Input Card */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Input Text</CardTitle>
                            <CardDescription>Enter or paste your text below</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    placeholder="Type or paste your text here..."
                                    className="min-h-[200px] w-full pl-3"
                                    rows={8}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearText}
                                        disabled={!inputText}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Clear text"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="relative">
                                    <Button variant="outline" size="sm" asChild className="cursor-pointer">
                                        <Label htmlFor="file-upload" className="cursor-pointer flex items-center gap-1">
                                            <FileInput className="h-4 w-4" />
                                            Upload File
                                            <input
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileUpload}
                                                className="hidden"
                                                accept=".txt,.md,.doc,.docx"
                                            />
                                        </Label>
                                    </Button>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {inputText.length} characters • {inputText.split(/\s+/).filter(Boolean).length} words
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Output Card */}
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle>Converted Text</CardTitle>
                                    <CardDescription>Your text in {labelCase(activeCase)} case</CardDescription>
                                </div>
                                <Select value={activeCase} onValueChange={(value) => setActiveCase(value as CaseType)}>
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder="Select case" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries({
                                            sentence: 'Sentence case',
                                            lower: 'lowercase',
                                            upper: 'UPPERCASE',
                                            title: 'Title Case',
                                            camel: 'camelCase',
                                            pascal: 'PascalCase',
                                            snake: 'snake_case',
                                            kebab: 'kebab-case',
                                            dot: 'dot.case',
                                            constant: 'CONSTANT_CASE',
                                            train: 'Train-Case',
                                            inverse: 'iNVERSE cASE',
                                            alternating: 'AlTeRnAtInG cAsE'
                                        }).map(([value, label]) => (
                                            <SelectItem key={value} value={value}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="relative">
                                <Textarea
                                    value={outputText}
                                    readOnly
                                    className="min-h-[200px] w-full bg-muted/50 font-mono pl-3"
                                    rows={8}
                                />
                                <div className="absolute right-3 top-3 flex space-x-2">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={copyToClipboard}
                                        disabled={!outputText}
                                        className="h-9 w-9 bg-background/80 backdrop-blur-sm"
                                        aria-label="Copy to clipboard"
                                    >
                                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {outputText.length} characters • {outputText.split(/\s+/).filter(Boolean).length} words
                            </div>
                        </CardContent>
                    </Card>

                    {/* Case Examples */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Case Examples</CardTitle>
                            <CardDescription>Preview of different case formats</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="sentence" className="w-full">
                                <TabsList className="grid grid-cols-3 h-auto p-1">
                                    {Object.entries({
                                        sentence: 'Sentence',
                                        lower: 'Lower',
                                        upper: 'Upper',
                                        title: 'Title',
                                        camel: 'Camel',
                                        pascal: 'Pascal',
                                        snake: 'Snake',
                                        kebab: 'Kebab',
                                        dot: 'Dot',
                                        constant: 'Constant',
                                        train: 'Train',
                                        inverse: 'Inverse',
                                        alternating: 'Alternating'
                                    }).map(([key, label]) => (
                                        <TabsTrigger
                                            key={key}
                                            value={key}
                                            className="text-xs py-1 h-auto"
                                            onClick={() => setActiveCase(key as CaseType)}
                                        >
                                            {label}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                {Object.entries(caseExamples).map(([key, example]) => (
                                    <TabsContent key={key} value={key} className="mt-4 p-4 border rounded-md bg-muted/20">
                                        <div className="text-sm font-medium mb-1">{labelCase(key)} Case</div>
                                        <div className="text-sm text-muted-foreground font-mono">{example}</div>
                                    </TabsContent>
                                ))}
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* About Card */}
            <Card className="mt-6">
                <CardHeader>
                    <CardTitle>About Text Case Converter</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                    <p>
                        This tool helps you convert text between different case formats, useful for programming, writing, and data formatting.
                    </p>
                    <div className="space-y-2">
                        <h3 className="font-medium text-foreground">Common Use Cases:</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Formatting code variables and constants</li>
                            <li>Preparing text for URLs and filenames</li>
                            <li>Standardizing data formats</li>
                            <li>Improving readability of text</li>
                            <li>Preparing content for different programming languages</li>
                        </ul>
                    </div>
                    <div className="space-y-2">
                        <h3 className="font-medium text-foreground">Tips:</h3>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Use <code className="bg-muted px-1 rounded">camelCase</code> for JS variables/functions</li>
                            <li>Use <code className="bg-muted px-1 rounded">PascalCase</code> for class names</li>
                            <li>Use <code className="bg-muted px-1 rounded">snake_case</code> for Python variables & DB columns</li>
                            <li>Use <code className="bg-muted px-1 rounded">kebab-case</code> for URLs & CSS classes</li>
                            <li>Use <code className="bg-muted px-1 rounded">CONSTANT_CASE</code> for constants</li>
                        </ul>
                    </div>
                </CardContent>
            </Card>

            {/* SEO Content */}
            <div className="mt-16">
                <InformationalContent
                    title={textCaseConverterContent.title}
                    description={textCaseConverterContent.description}
                    content={textCaseConverterContent.content}
                    faqs={textCaseConverterContent.faqs}
                    relatedTools={textCaseConverterContent.relatedTools}
                />
            </div>
        </div>
    );
}
